import React, { useState } from "react";
import { useParams } from "react-router-dom";
import { useDispatch, useSelector } from "react-redux";
import {
    Box,
    Button,
    Chip,
    Grid,
    CircularProgress,
    Modal,
    Typography,
} from "@mui/material/";
import makeStyles from "@mui/styles/makeStyles";
import {
    useAuditProgressDetails,
    useGroupAuditProgressDetails,
} from "../common/hooks";
import { useTranslation } from "react-i18next";

import styles from "./styles";
import actions from "../Store/actions";

import NetworkWifiIcon from "@mui/icons-material/NetworkWifi";
import SyncIcon from "@mui/icons-material/Sync";
import SignalWifiOffIcon from "@mui/icons-material/SignalWifiOff";
import {
    FolderOutlined as FolderOutlinedIcon,
    FolderSpecialOutlined as FolderSpecialOutlinedIcon,
    Check as CheckIcon,
    RotateLeft as RotateLeftIcon,
    ErrorOutline as ErrorOutlineIcon,
} from "@mui/icons-material";
import CloseIcon from "@mui/icons-material/Close";
import { logToSentry } from "../config/Sentry";

/**
 * Audit Completion Component
 */
export default () => {
    const { auditId } = useParams();
    const classes = makeStyles(styles)();
    const [syncModal, setSyncModal] = useState(false);
    const [loading, setLoading] = useState(false);
    const { t, i18n } = useTranslation();
    const dispatch = useDispatch();
    const { audits, app, auditLocations } = useSelector((state) => state);
    const { alt, assigned, required } = useAuditProgressDetails("db", auditId);
    const { groupedAssignedAsset, groupedRequired } =
        useGroupAuditProgressDetails("store", auditId);

    const isGroupAudit = audits?.item?.auditReference.toLowerCase() !== "none";

    const { backgroundSyncRequest, netConnected, assetSyncTime } = app;

    /** Open sync modal */
    const openSyncModalHandler = () => setSyncModal(true);

    /** Close sync modal */
    const closeSyncModalHandler = (event, reason) => {
        if (reason && reason == "backdropClick") return;
        setSyncModal(false);
    };
    /** On asset sync modal */
    const onAssetSync = async () => {
        try {
            setLoading(true);
            await dispatch(actions.assetSync.create.postRequest());
            setSyncModal(false);
            setLoading(false);
        } catch (error) {
            console.log(error);
            logToSentry(`on Manual Sync:`, {
                error,
                errStr: error.toString(),
            });
        }
    };
    /** Format date time  */
    const formatDate = (dt) => {
        const date = new Date(dt).toLocaleDateString(i18n.language, {
            day: "numeric",
            month: "short",
            year: "numeric",
        });
        const time = dt.toLocaleTimeString("en-us");
        return `${date} ${time}`;
    };

    const dateTime = formatDate(assetSyncTime);

    return (
        <>
            <Box
                className={classes.assetListHeading}
                onClick={openSyncModalHandler}
            >
                <Grid container>
                    {isGroupAudit && (
                        <Grid item xs={12}>
                            <Chip
                                className={classes.chip}
                                variant="body2"
                                icon={
                                    <FolderSpecialOutlinedIcon
                                        className={classes.iconColor}
                                        fontSize="medium"
                                    />
                                }
                            />
                            <Chip
                                className={classes.chip}
                                variant="body2"
                                icon={
                                    <CheckIcon
                                        className={classes.iconColor}
                                        fontSize="small"
                                    />
                                }
                                label={groupedAssignedAsset}
                            />
                            <Chip
                                className={classes.chip}
                                icon={
                                    <RotateLeftIcon
                                        className={classes.iconColor}
                                        fontSize="small"
                                    />
                                }
                                label={groupedRequired}
                            />
                        </Grid>
                    )}
                    {/* <Grid item xs={2}></Grid> */}
                    <Grid item xs={8}>
                        <Chip
                            className={classes.chip}
                            variant="body2"
                            icon={
                                <FolderOutlinedIcon
                                    className={classes.iconColor}
                                    fontSize="medium"
                                />
                            }
                        />
                        <Chip
                            className={classes.chip}
                            variant="body2"
                            icon={
                                <CheckIcon
                                    className={classes.iconColor}
                                    fontSize="small"
                                />
                            }
                            label={assigned}
                        />
                        <Chip
                            className={classes.chip}
                            icon={
                                <RotateLeftIcon
                                    className={classes.iconColor}
                                    fontSize="small"
                                />
                            }
                            label={required}
                        />
                        <Chip
                            className={classes.chip}
                            icon={
                                <ErrorOutlineIcon
                                    className={classes.iconColor}
                                    fontSize="small"
                                />
                            }
                            label={alt}
                        />
                    </Grid>
                    <Grid item xs={4} className={classes.alignGrid}>
                        <Box className={classes.loadingState}>
                            {backgroundSyncRequest ? (
                                <CircularProgress
                                    className={classes.syncIcon}
                                />
                            ) : null}
                            {netConnected ? (
                                <NetworkWifiIcon
                                    fontSize="small"
                                    className={`${
                                        (classes.netConnected, classes.active)
                                    }`}
                                />
                            ) : (
                                <SignalWifiOffIcon
                                    className={classes.netConnected}
                                    fontSize="small"
                                />
                            )}
                        </Box>
                    </Grid>
                </Grid>
            </Box>
            <Modal
                open={syncModal}
                onClose={closeSyncModalHandler}
                aria-labelledby="asset-sync-modal-title"
                aria-describedby="asset-sync-modal-description"
            >
                <Grid container className={classes.modalBox}>
                    <Grid pt={1}>
                        {loading && (
                            <Box sx={{ width: "100%", textAlign: "center" }}>
                                <CircularProgress />
                            </Box>
                        )}
                        <Typography
                            id="modal-description"
                            className={classes.modalMessage}
                            component="div"
                        >
                            {loading
                                ? t("messages.syncAssets")
                                : netConnected
                                ? t("messages.onlineSyncAssets", {
                                      dateTime,
                                  })
                                : t("messages.offlineSyncAssets")}
                        </Typography>
                    </Grid>
                    <Grid className={classes.modalActions}>
                        {!loading && (
                            <>
                                <Button
                                    variant="outlined"
                                    color="primary"
                                    onClick={closeSyncModalHandler}
                                    endIcon={<CloseIcon />}
                                >
                                    {t("cancel")}
                                </Button>
                                {netConnected && (
                                    <Button
                                        variant="contained"
                                        color="primary"
                                        onClick={onAssetSync}
                                        endIcon={<SyncIcon />}
                                    >
                                        {t("sync")}
                                    </Button>
                                )}
                            </>
                        )}
                    </Grid>
                </Grid>
            </Modal>
        </>
    );
};
